import { Link, useLocation, useParams } from "react-router-dom";
import { useQueries } from "@tanstack/react-query";
import { CalendarCheck, Info } from "lucide-react";
import { toolboxPatFixPath } from "@/components/ado/ado-pat-callout";
import { useSharedAgenda } from "@/hooks/queries/use-shared-agenda";
import { canOn, useToolboxAccess } from "@/hooks/queries/use-toolbox-access";
import { retryTransientOnce, workItemKeys } from "@/hooks/queries/use-work-items";
import { usePageTitle } from "@/hooks/use-page-title";
import type { SharedAgendaFix, SharedAgendaView } from "@/lib/api/grooming-shared-agenda";
import { getAdoPatErrorCode, workItemsApi, type AdoPatErrorCode, type WorkItemDetail } from "@/lib/api/work-items";
import { groomingApiErrorCode } from "@/lib/grooming/error-messages";
import { FIX_LIST_HEADING } from "@/lib/grooming/plain-words";
import { groomingPath } from "@/lib/grooming/paths";
import { useToolboxBridge } from "@/lib/toolbox/bridge";
import { formatSessionDate } from "./session";
import { TOOLBOX_PAGE_TITLE, TOOLBOX_TEXT } from "@/lib/toolbox/type-scale";
import { cn } from "@/lib/utils";

/**
 * `/grooming/:project/agenda/:batchId`: the shared session agenda (epic
 * PI-SPEC-SW-E280, feature PI-SPEC-SW-1402; size-free read and open gate:
 * PKG-PM-C, epic PI-SPEC-SW-E291, feature PI-SPEC-SW-1491), opened from the
 * Session screen's "Share agenda" link in a browser or in SpecTree Toolbox.
 *
 * Who can open it: **any signed-in SpecTree user, role or not** (01-roles.md
 * section 9.6). Meeting attendees usually hold no Toolbox role, so the route
 * has no Toolbox-role guard and no ADO PAT guard; sign-in is enforced by
 * AppLayout and by the API.
 *
 * What it reads: only the size-free agenda (`GET /grooming/shared-agendas/:batchId`):
 * the project, the date, the items in discussion order (title and ID) and the
 * fix list (FIX_LIST_HEADING) with why. It never reads the batch
 * (`GET /grooming/batches/:id`) or the session (`GET /grooming/projects/:project/session`),
 * whose responses carry Claude's sizes, so no size can reach the page and the
 * team is not anchored before it sizes an item (ER decision 4).
 *
 * Each item's type, plain "what is it" and live readiness come from the
 * viewer's own ADO read, which needs their ADO PAT. Without one the page
 * lists every item by title and ID and says how to connect Azure DevOps
 * (Setup inside SpecTree Toolbox, Settings in a browser); gap R9 (no-PAT
 * reads) stays for later. "Open the Session screen" shows only to people with
 * `read-backlog` on the project, the ones the Session screen opens for.
 */

/** One agenda line, without any size. */
export interface SharedAgendaRow {
  itemId: string;
  title: string;
  typeName: string | null;
  /** The brief's plain "what is it", from the viewer's own ADO read, when the item has one. */
  whatIsIt: string | null;
}

/**
 * Split the size-free agenda into the agenda rows and the fix list: the
 * agenda's own fixes (items the run found below the grooming bar), then any
 * item whose live readiness, read with the viewer's PAT, says it is not ready
 * for grooming. Rows keep the discussion order. Pure; there is no size to read.
 */
export function sharedAgenda(
  view: Pick<SharedAgendaView, "items" | "fixes">,
  details: ReadonlyMap<string, WorkItemDetail>
): { rows: SharedAgendaRow[]; fixes: SharedAgendaFix[] } {
  const rows: SharedAgendaRow[] = [];
  const fixes: SharedAgendaFix[] = view.fixes.map((fix) => {
    const title = details.get(fix.itemId)?.title ?? fix.title;
    return { itemId: fix.itemId, title, why: fix.why };
  });
  for (const item of [...view.items].sort((a, b) => a.position - b.position)) {
    const detail = details.get(item.itemId);
    const title = detail?.title ?? item.title;
    if (detail?.readiness.readyForGrooming === false) {
      const missing = detail.readiness.grooming.missing.map((m) => m.label);
      fixes.push({ itemId: item.itemId, title, why: missing.length > 0 ? missing.join(" · ") : "Missing information" });
      continue;
    }
    const whatIsIt = detail?.groomingBrief?.brief?.body.plainBrief?.whatIsIt.trim() ?? "";
    rows.push({
      itemId: item.itemId,
      title,
      typeName: detail?.typeName ?? item.typeName,
      whatIsIt: whatIsIt === "" ? null : whatIsIt,
    });
  }
  return { rows, fixes };
}

/** The agenda's date, as the size-free read settled it (the agenda's own date or the next meeting). */
export function sharedAgendaDate(view: Pick<SharedAgendaView, "sessionDate">): string | null {
  return view.sessionDate;
}

/**
 * The PAT note under the agenda: inside SpecTree Toolbox it opens Setup's
 * Connect Azure DevOps step (as AdoPatCallout does, epic PI-SPEC-SW-E288); in
 * a browser it opens SpecTree Settings.
 */
function PatNote({ code }: { code: AdoPatErrorCode }) {
  const inToolbox = useToolboxBridge() !== null;
  const location = useLocation();
  const expired = code === "ADO_PAT_INVALID";
  const why = expired
    ? "Azure DevOps didn't accept your saved token, so item summaries are missing."
    : "Item summaries need your Azure DevOps connection.";
  return (
    <p className={cn("flex flex-wrap items-center gap-2 text-[13px]", TOOLBOX_TEXT.faint)} data-testid="agenda-share-pat-note">
      <Info className="h-[13px] w-[13px] flex-none" aria-hidden="true" />
      <span>{why}</span>
      {inToolbox ? (
        <Link to={toolboxPatFixPath(location.pathname, location.search)} className="text-link hover:underline">
          {expired ? "Connect Azure DevOps again on Setup" : "Connect Azure DevOps on Setup"}
        </Link>
      ) : (
        <Link to="/settings" className="text-link hover:underline">
          Save your token in Settings
        </Link>
      )}
    </p>
  );
}

function Loading() {
  return (
    <div className="flex min-h-[240px] items-center justify-center" role="status" aria-live="polite">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      <span className="sr-only">Loading the agenda…</span>
    </div>
  );
}

export function GroomingAgendaSharePage() {
  const { batchId = "" } = useParams();
  usePageTitle("Session agenda");
  const agenda = useSharedAgenda(batchId);
  const { data: access } = useToolboxAccess();

  const project = agenda.data?.project ?? "";
  const agendaItems = agenda.data ? [...agenda.data.items, ...agenda.data.fixes] : [];
  const detailReads = useQueries({
    queries: agendaItems.map((item) => ({
      queryKey: workItemKeys.detail("ado", project, item.itemId),
      queryFn: () => workItemsApi.detail("ado", project, item.itemId),
      select: (response: { data: WorkItemDetail }) => response.data,
      retry: retryTransientOnce,
      staleTime: 60 * 1000,
    })),
  });

  if (agenda.isLoading) {
    return (
      <div className="mx-auto w-full max-w-[880px] px-4 py-6" data-testid="grooming-agenda-share">
        <Loading />
      </div>
    );
  }
  if (agenda.isError || !agenda.data) {
    const code = groomingApiErrorCode(agenda.error);
    return (
      <div className="mx-auto w-full max-w-[880px] px-4 py-6" data-testid="grooming-agenda-share">
        <p className="text-sm text-destructive" role="alert">
          {code === "NOT_FOUND" || code === "VALIDATION_ERROR"
            ? "This agenda was not found. Ask for a new link."
            : "The agenda could not be loaded. Try again in a moment."}
        </p>
      </div>
    );
  }

  const details = new Map<string, WorkItemDetail>();
  detailReads.forEach((read, index) => {
    const itemId = agendaItems[index]?.itemId;
    if (itemId && read.data) details.set(itemId, read.data);
  });
  const patCode = detailReads.map((read) => getAdoPatErrorCode(read.error)).find((code) => code !== undefined);
  const { rows, fixes } = sharedAgenda(agenda.data, details);
  const date = sharedAgendaDate(agenda.data);
  const maySeeSession = canOn(access, project, "read-backlog");

  return (
    <div className="mx-auto flex w-full max-w-[880px] flex-col gap-4 px-4 py-6" data-testid="grooming-agenda-share">
      <header className="flex flex-wrap items-end gap-3">
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
            Grooming session agenda
          </span>
          <h1 className={cn("flex items-center gap-2", TOOLBOX_PAGE_TITLE)} data-testid="agenda-share-date">
            <CalendarCheck className="h-5 w-5 text-primary" aria-hidden="true" />
            {date ? formatSessionDate(date) : "Next grooming session"}
          </h1>
          <span className="text-sm text-muted-foreground" data-testid="agenda-share-summary">
            {project} · {rows.length} {rows.length === 1 ? "item" : "items"} to discuss
            {fixes.length > 0 ? ` · ${String(fixes.length)} to fix first` : ""}
          </span>
        </div>
        {maySeeSession && (
          <Link
            to={groomingPath(project, "session")}
            className="text-sm text-link hover:underline"
            data-testid="agenda-share-session-link"
          >
            Open the Session screen
          </Link>
        )}
      </header>

      <section className="glass-card overflow-hidden rounded-[10px]" aria-labelledby="agenda-share-heading">
        <div className="flex items-center gap-2 border-b border-[#1F1F23] px-4 py-3">
          <h2 id="agenda-share-heading" className="flex-1 font-display text-sm font-semibold">
            Agenda
          </h2>
          <span className="text-xs text-muted-foreground">in the order we&apos;ll discuss them</span>
        </div>
        {rows.length === 0 ? (
          <p className="p-4 text-sm text-muted-foreground" data-testid="agenda-share-empty">
            Nothing is on this agenda yet.
          </p>
        ) : (
          <ol aria-label="Agenda items">
            {rows.map((row, index) => (
              <li
                key={row.itemId}
                className="grid grid-cols-[28px_minmax(0,1fr)] gap-2.5 border-b border-[#141417] px-4 py-3"
                data-testid="agenda-share-row"
                data-item-id={row.itemId}
              >
                <span className={cn("font-display text-[15px] font-semibold", TOOLBOX_TEXT.faint)}>{index + 1}</span>
                <span className="flex min-w-0 flex-col gap-1">
                  <span className="text-[14px] font-medium">{row.title}</span>
                  <span className="text-xs text-muted-foreground">
                    <span className="font-mono">AB#{row.itemId}</span>
                    {row.typeName ? ` · ${row.typeName}` : ""}
                  </span>
                  {row.whatIsIt && (
                    <span className="text-sm leading-relaxed text-[#D4D4D8]" data-testid="agenda-share-what">
                      {row.whatIsIt}
                    </span>
                  )}
                </span>
              </li>
            ))}
          </ol>
        )}
      </section>

      {fixes.length > 0 && (
        <section className="glass-card overflow-hidden rounded-[10px]" aria-labelledby="agenda-share-fixes" data-testid="agenda-share-fixes">
          <div className="flex flex-col gap-0.5 border-b border-[#1F1F23] px-4 py-3">
            <h2 id="agenda-share-fixes" className="font-display text-sm font-semibold">
              {FIX_LIST_HEADING}
            </h2>
            <span className="text-xs text-muted-foreground">These don&apos;t meet the grooming standard yet</span>
          </div>
          <ul>
            {fixes.map((fix) => (
              <li
                key={fix.itemId}
                className="flex flex-wrap items-center gap-3 border-b border-[#141417] px-4 py-2.5"
                data-testid="agenda-share-fix"
                data-item-id={fix.itemId}
              >
                <span className="font-mono text-xs text-muted-foreground">AB#{fix.itemId}</span>
                <span className="min-w-0 flex-1 truncate text-[14px]">{fix.title}</span>
                <span className="text-sm text-muted-foreground">{fix.why}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {patCode && <PatNote code={patCode} />}
    </div>
  );
}
